/**
 * Meta Conversions API (server-side) client helper.
 *
 * - Centralizes fbp/fbc cookie + external_id reads (same on desktop/mobile).
 * - Implements retry logic (3 attempts, exponential backoff) for transient
 *   network failures.
 * - Always emits structured console logs `[Meta CAPI <event>] ...` so the
 *   browser console + Sentry/Clarity replays show outcomes.
 * - Validates Meta's response (`events_received >= 1`) before declaring success.
 *
 * NOTE: For Purchase events, ALWAYS pass an `event_id` that matches the
 * client-side Pixel fbq('track','Purchase',{},{eventID}) so Meta can dedupe.
 */

export interface MetaCapiEvent {
  event_name: 'AddToCart' | 'ViewContent' | 'InitiateCheckout' | 'Purchase' | 'Lead' | 'CompleteRegistration';
  event_id?: string;
  event_time?: number;
  event_source_url?: string;
  custom_data?: Record<string, unknown>;
  user_data?: Record<string, unknown>;
}

const MAX_ATTEMPTS = 3;
const BASE_BACKOFF_MS = 400;

/** Só o domínio de produção pode gerar eventos reais no pixel. */
const PRODUCTION_HOSTS = ['nathof.com.br', 'www.nathof.com.br'];

export function isProductionTrackingHost(): boolean {
  if (typeof window === 'undefined') return false;
  return PRODUCTION_HOSTS.includes(window.location.hostname.toLowerCase());
}


function readMatchingSignals() {
  if (typeof document === 'undefined') return { fbp: undefined, fbc: undefined, externalId: undefined };
  const fbp = document.cookie.match(/(?:^|;\s*)_fbp=([^;]*)/)?.[1] || undefined;
  const fbc = document.cookie.match(/(?:^|;\s*)_fbc=([^;]*)/)?.[1] || undefined;
  let externalId: string | undefined;
  try {
    externalId = localStorage.getItem('nat_external_id') || undefined;
  } catch {
    externalId = undefined;
  }
  return { fbp, fbc, externalId };
}

async function postOnce(url: string, payload: unknown): Promise<{ ok: boolean; status: number; body: any }> {
  const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(anonKey ? { Authorization: `Bearer ${anonKey}`, apikey: anonKey } : {}),
    },
    body: JSON.stringify(payload),
    keepalive: true,
  });
  let body: any = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  return { ok: res.ok, status: res.status, body };
}

/**
 * Fire a CAPI event with retry, structured logging, and response validation.
 * Returns true if Meta confirmed receipt (events_received >= 1).
 */
export async function sendMetaCAPI(event: MetaCapiEvent): Promise<boolean> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const tag = `[Meta CAPI ${event.event_name}]`;

  // Gate de hostname: preview/localhost NUNCA envia evento para o pixel.
  if (!isProductionTrackingHost()) {
    console.log(`${tag} skipped — non-production hostname`);
    return false;
  }

  if (!supabaseUrl) {
    console.warn(`${tag} SUPABASE_URL missing — event NOT sent`);
    return false;
  }


  const { fbp, fbc, externalId } = readMatchingSignals();
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown';
  const url = typeof window !== 'undefined' ? window.location.href : 'https://nathof.com.br';

  const payload = {
    event_name: event.event_name,
    event_time: event.event_time ?? Math.floor(Date.now() / 1000),
    event_id: event.event_id,
    user_agent: ua,
    event_source_url: event.event_source_url ?? url,
    fbp,
    fbc,
    external_id: externalId,
    custom_data: event.custom_data,
    user_data: event.user_data,
  };

  console.log(`${tag} sending`, {
    event_id: event.event_id,
    has_fbp: !!fbp,
    has_fbc: !!fbc,
    has_external_id: !!externalId,
    custom_data: event.custom_data,
  });

  const endpoint = `${supabaseUrl}/functions/v1/meta-capi`;

  let lastError: unknown = null;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      const { ok, status, body } = await postOnce(endpoint, payload);
      if (ok) {
        const received = Number(body?.events_received ?? 0);
        if (received >= 1) {
          console.log(`${tag} OK`, {
            attempt,
            fbtrace_id: body?.fbtrace_id,
            events_received: received,
          });
          return true;
        }
        // 200 OK but Meta did not accept — do not retry, it's a payload issue.
        console.error(`${tag} REJECTED by Meta`, { attempt, body });
        return false;
      }

      // 4xx → payload problem, do not retry
      if (status >= 400 && status < 500) {
        console.error(`${tag} FAILED (no retry)`, { attempt, status, body });
        return false;
      }

      // 5xx → retry
      console.warn(`${tag} retryable failure`, { attempt, status, body });
      lastError = body;
    } catch (err) {
      console.warn(`${tag} network error`, { attempt, err });
      lastError = err;
    }

    if (attempt < MAX_ATTEMPTS) {
      await new Promise((r) => setTimeout(r, BASE_BACKOFF_MS * Math.pow(2, attempt - 1)));
    }
  }

  console.error(`${tag} GAVE UP after ${MAX_ATTEMPTS} attempts`, { lastError });
  return false;
}
